// App Dashboard Page - Displays CAB details for application
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  Alert,
  CircularProgress,
  Button,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link,
  Paper,
  Avatar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import {
  ArrowBack as ArrowBackIcon,
  Dashboard as DashboardIcon,
  Business as BusinessIcon,
  Science as ScienceIcon,
  Badge as BadgeIcon,
  Timeline as TimelineIcon,
  Schedule as ScheduleIcon,
  TrendingUp as TrendingUpIcon,
  Description as DescriptionIcon,
  Person2Outlined,
  VisibilityOutlined as ScrutinyIcon,
  History as HistoryIcon,
  Person,
  Check as CheckIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getApplicationStage } from "../../../services/api";
import DoApiService from "../../../services/DoApiService";
import {
  decryptAppId,
  decryptText,
  encryptAppId,
  encryptText,
} from "../../../utils/encryption";
import ReportsDetails from "../../Do/application/ReportsDetails";
import PaymentDetails from "../../Do/application/PaymentDetails";
import EmailNotificationDetails from "../../Do/application/EmailNotificationDetails";
import AssessorDetailsView from "./AssessorDetailsView";
import acProcessService from "../../../services/acProcessService";
import { generateCommunicationLetter } from "../../../components/certificate/CommunicationLetterGenerator";
import { generateDecisionLetter } from "../../../components/certificate/DecisionLetterGenerator";
import { toast } from "react-toastify";

const AppDashboard = () => {
  // State management
  const [cabDetails, setCabDetails] = useState(null);
  const [dealingOfficer, setDealingOfficer] = useState(null);
  const [contactPersons, setContactPersons] = useState([]); // Changed to array for multiple contacts
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openOfficerDialog, setOpenOfficerDialog] = useState(false);
  const [openContactDialog, setOpenContactDialog] = useState(false);
  // Dialog open/close handlers
  const handleOpenOfficerDialog = () => setOpenOfficerDialog(true);
  const handleCloseOfficerDialog = () => setOpenOfficerDialog(false);
  const handleOpenContactDialog = () => setOpenContactDialog(true);
  const handleCloseContactDialog = () => setOpenContactDialog(false);

  // Approve/Disapprove states
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [disapproveDialogOpen, setDisapproveDialogOpen] = useState(false);
  const [coList, setCoList] = useState([]);
  const [selectedCO, setSelectedCO] = useState("");
  const [approvePassword, setApprovePassword] = useState("");
  const [loadingCOList, setLoadingCOList] = useState(false);
  const [currentApplicationForApproval, setCurrentApplicationForApproval] =
    useState(null);
  const [forwardReason, setForwardReason] = useState("");
  const [success, setSuccess] = useState(null);
  const [applicationData, setApplicationData] = useState(null);

  // Router hooks
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get encrypted parameters from URL
  const encryptedAppId = searchParams.get("appId");
  const encryptedOrgId = searchParams.get("orgId");
  const encryptedLabTypeId = searchParams.get("labTypeId");

  // Decrypt parameters
  const appId = encryptedAppId ? decryptAppId(encryptedAppId) : null;
  const orgId = encryptedOrgId ? decryptText(encryptedOrgId) : null;
  const labTypeId = encryptedLabTypeId ? decryptText(encryptedLabTypeId) : null;
  const [applicationStage, setApplicationStage] = useState(null);

  // Load dealing officer details (mode 31)
  const loadDealingOfficer = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await DoApiService.getAllotmentDetails({
        mode: 31,
        appId: parseFloat(appId),
      });

      if (response.success && response.data.length > 0) {
        setDealingOfficer(response.data[0]); // Take first officer
      }
    } catch (err) {
      console.error("Load dealing officer error:", err);
    }
  }, [appId]);

  const LoadApplicationStage = useCallback(async () => {
    if (!appId) return;
    try {
      const stageResponse = await getApplicationStage(appId);
      if (stageResponse.data.success) {
        setApplicationStage(stageResponse.data.stage);
      }
    } catch (err) {
      console.error("Load application stage error:", err);
    }
  }, [appId]);

  // Load contact person details (mode 27)
  const loadContactPerson = useCallback(async () => {
    if (!appId) return;

    try {
      const response = await DoApiService.getAllotmentDetails({
        mode: 27,
        appId: parseFloat(appId),
      });

      if (response.success && response.data.length > 0) {
        setContactPersons(response.data); // Store all contact persons (lab head + CEO details)
      }
    } catch (err) {
      console.error("Load contact person error:", err);
    }
  }, [appId]);

  // Load CAB details on page load (equivalent to .NET getCABDetailAll)
  const loadCABDetails = useCallback(async () => {
    if (!appId) {
      setError("Invalid application ID. Please check the URL.");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Call API to get CAB details (mode 100 equivalent)
      const response = await DoApiService.getCABDetails({
        appId: parseFloat(appId), // Convert to double as per .NET code
      });

      if (response.success) {
        setCabDetails(response.data);

        // Store in session equivalent (localStorage)
        localStorage.setItem("AppDashboard_AppId", appId);
        localStorage.setItem("AppDashboard_OrgId", orgId || "");
        localStorage.setItem("AppDashboard_LabTypeId", labTypeId || "");

        // Load additional details
        await Promise.all([loadDealingOfficer(), loadContactPerson()]);
      } else {
        throw new Error(response.message || "Failed to load CAB details");
      }
    } catch (err) {
      setError(`Error loading CAB details: ${err.message}`);
      console.error("Load CAB details error:", err);
    } finally {
      setLoading(false);
    }
  }, [appId, orgId, labTypeId, loadDealingOfficer, loadContactPerson]);

  // Load data when component mounts
  useEffect(() => {
    loadCABDetails();
    LoadApplicationStage();
  }, [loadCABDetails, LoadApplicationStage]);

  // Handle back navigation
  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  // Fetch CO List
  const fetchCOList = async (appIdToUse) => {
    try {
      setLoadingCOList(true);
      const response = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/ceolist`,
        {
          params: {
            appid: appIdToUse,
            usertypeid: "10", // CO user type
          },
        },
      );

      if (response.data && response.data.ceo) {
        setCoList(response.data.ceo);
      } else {
        setCoList([]);
      }
    } catch (error) {
      console.error("Error fetching CO list:", error);
      setError("Error loading CO list");
      setCoList([]);
    } finally {
      setLoadingCOList(false);
    }
  };

  // Handle Approve Click
  const handleApproveClick = () => {
    setCurrentApplicationForApproval(cabDetails);
    fetchCOList(appId);
    setApproveDialogOpen(true);
  };

  // Submit Approve
  const handleApproveSubmit = async () => {
    try {
      if (!selectedCO) {
        setError("Please select a CO");
        return;
      }

      if (!approvePassword.trim()) {
        setError("Please enter password");
        return;
      }

      setLoading(true);

      // Verify password
      const orgIdFromStorage = localStorage.getItem("orgId");
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: orgIdFromStorage,
          password: approvePassword,
        },
      );

      if (!passwordResponse.data.success) {
        setError("Invalid password");
        setLoading(false);
        return;
      }

      // Call approve API
      const payload = {
        forwaredTo: selectedCO,
        forwardBy: orgIdFromStorage,
        forwardReason: forwardReason.trim(),
        appId: appId.toString(),
        forwardType: "Certificate",
        forwardByrole: "8", // Director role
        forwardToRole: "10", // CO role
      };

      const response = await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/directorapprove`,
        payload,
      );
      if (response.data.success || response.data.isstatus) {
        setSuccess("Certificate approved successfully");
        await acProcessService.updateApplicationStage(appId, "7c");
        setApproveDialogOpen(false);
        setSelectedCO("");
        setApprovePassword("");
        setForwardReason("");
        setCurrentApplicationForApproval(null);
      } else {
        setError(response.data.message || "Failed to approve certificate");
      }
      // Reload page to refresh data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Error approving application:", error);
      setError(
        error.response?.data?.message ||
          "Error approving application. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  // Handle Disapprove Click
  const handleDisapproveClick = () => {
    setCurrentApplicationForApproval(cabDetails);
    fetchCOList(appId);
    setDisapproveDialogOpen(true);
  };

  // Submit Disapprove
  const handleDisapproveSubmit = async () => {
    try {
      if (!forwardReason.trim()) {
        setError("Please enter reason for disapproval");
        return;
      }

      if (!approvePassword.trim()) {
        setError("Please enter password");
        return;
      }

      setLoading(true);

      // Verify password
      const orgIdFromStorage = localStorage.getItem("orgId");
      const passwordResponse = await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/verify-password`,
        {
          orgId: orgIdFromStorage,
          password: approvePassword,
        },
      );

      if (!passwordResponse.data.success) {
        setError("Invalid password");
        setLoading(false);
        return;
      }

      // Call disapprove API
      const payload = {
        forwaredTo: "",
        forwardBy: orgIdFromStorage,
        forwardReason: forwardReason.trim(),
        appId: appId.toString(),
        forwardType: "Certificate",
        forwardByrole: "8", // Director role
        forwardToRole: "10", // CO role
      };

      await axios.post(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/directorapprove`,
        payload,
      );

      setSuccess("Application disapproved successfully");
      setDisapproveDialogOpen(false);
      setSelectedCO("");
      setApprovePassword("");
      setForwardReason("");
      setCurrentApplicationForApproval(null);

      // Reload page to refresh data
      setTimeout(() => {
        window.location.reload();
      }, 1500);
    } catch (error) {
      console.error("Error disapproving application:", error);
      setError(
        error.response?.data?.message ||
          "Error disapproving application. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };
  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateString;
    }
  };

  // Handle View Communication Letter
  const handleViewCommunicationLetter = async () => {
    try {
      toast.info("Fetching communication letter...");

      // Load application details if not already loaded
      let appDetails = applicationData;
      if (!appDetails) {
        appDetails = await loadApplicationDetails(appId);
      }

      if (!appDetails) {
        toast.warning("Application data not loaded. Please try again.");
        return;
      }

      // Load communication data and get it directly (don't rely on state)
      const comminicationData = await loadCommunicationData(appId, orgId);

      const letterData = {
        applicationNo: appDetails.ApplicationNo || "N/A",
        letterDate: comminicationData.date ? new Date(comminicationData.date).toLocaleDateString("en-GB") : '',
        contactPerson: appDetails.ContactPerson || "N/A",
        laboratoryName: appDetails.LaboratoryName || "N/A",
        address: appDetails.Address || "N/A",
        extAddress: appDetails.ExtAddress || "N/A",
        mobile: appDetails.ContactMobile || "N/A",
        email: appDetails.ContactEmail || "N/A",
        subject: comminicationData.subject || "N/A",
        body: comminicationData.body || "N/A",
        advisoryList: [],
        doName: appDetails.DoName || "N/A",
        doEmail: appDetails.DoEmail || "N/A",
        showAdverseNote: false,
        showNewAddress: new Date() > new Date("2025-07-18"),
      };

      await generateCommunicationLetter(letterData);
      toast.success("Communication letter downloaded successfully");
    } catch (err) {
      console.error("Error generating letter:", err);
      toast.error("Failed to generate communication letter");
    }
  };

  // Handle View Decision Letter
  const handleViewDecisionLetter = async () => {
    try {
      toast.info("Fetching decision letter...");

      // Load application details if not already loaded
      let appDetails = applicationData;
      if (!appDetails) {
        appDetails = await loadApplicationDetails(appId);
      }

      if (!appDetails) {
        toast.warning("Application data not loaded. Please try again.");
        return;
      }

      // Load decision data and get it directly (don't rely on state)
      const decisionData = await loadDecisionData(appId, orgId);

      const letterData = {
        applicationNo: appDetails.ApplicationNo || "N/A",
        letterDate: decisionData.date ? new Date(decisionData.date).toLocaleDateString("en-GB") : '',
        contactPerson: appDetails.ContactPerson || "N/A",
        laboratoryName: appDetails.LaboratoryName || "N/A",
        address: appDetails.Address || "N/A",
        extAddress: appDetails.ExtAddress || "N/A",
        mobile: appDetails.ContactMobile || "N/A",
        email: appDetails.ContactEmail || "N/A",
        subject: decisionData.subject || "N/A",
        body: decisionData.body || "N/A",
        advisoryList: [],
        doName: appDetails.DoName || "N/A",
        doEmail: appDetails.DoEmail || "N/A",
        showAdverseNote: false,
        showNewAddress: new Date() > new Date("2025-07-18"),
      };

      await generateDecisionLetter(letterData);
      toast.success("Decision letter downloaded successfully");
    } catch (err) {
      console.error("Error generating letter:", err);
      toast.error("Failed to generate decision letter");
    }
  };

  const handleDownloadCertificate = () => {
    window.open(
      `/main/viewCertificate?appId=${encryptAppId(appId)}&orgId=${encryptText(orgId)}&action=${encryptText("download")}`,
      "_blank",
    );
  };

  // Load application details for communication letter
  const loadApplicationDetails = async (appId) => {
    try {
      const [appDetailsResponse, labHeadResponse, doResponse] =
        await Promise.all([
          axios.get(
            `${process.env.REACT_APP_API_URL}/ac-process/application/details/${appId}`,
          ),
          axios.get(`${process.env.REACT_APP_API_URL}/do/allotment-details`, {
            params: { mode: 27, appId: appId },
          }),
          axios.get(`${process.env.REACT_APP_API_URL}/do/allotment-details`, {
            params: { mode: 31, appId: appId },
          }),
        ]);

      const appData = appDetailsResponse.data?.data || {};
      const labHeadData = labHeadResponse.data?.data?.[0] || {};
      const doData = doResponse.data?.data?.[0] || {};

      const appDetails = {
        ApplicationNo: appData.ApplicationNo || "N/A",
        LaboratoryName: appData.LaboratoryName || "N/A",
        ContactPerson: labHeadData.name || "N/A",
        Address: labHeadData.address || "N/A",
        ExtAddress:
          `${labHeadData.city || "N/A"} ${labHeadData.state || "N/A"} ${labHeadData.pincode || "N/A"}`.trim(),
        ContactMobile: labHeadData.mobile || "N/A",
        ContactEmail: labHeadData.email || "N/A",
        DoName: doData.name || "N/A",
        DoEmail: doData.email || "N/A",
        CommunicationLetterSent: appData.CommunicationLetterSent || 0,
      };

      setApplicationData(appDetails);
      return appDetails;
    } catch (err) {
      console.error("Error loading application details:", err);
      return null;
    }
  };

  // Load decision data
  const loadDecisionData = async (appId, orgId) => {
    try {
      const commResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Getdecision`,
        {
          params: { appid: appId, orgid: orgId },
        },
      );

      const subject = commResponse.data?.subject || "";
      const body = commResponse.data?.body || "";
      const date = commResponse.data?.date || "";
      // Return the data for immediate use
      return {
        subject,
        body,
        date,
      };
    } catch (err) {
      console.error("Error loading decision data:", err);
      return {
        subject: "",
        body: "",
        date: "",
      };
    }
  };

  // Load communication data
  const loadCommunicationData = async (appId, orgId) => {
    try {
      const commResponse = await axios.get(
        `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/GetCommunication`,
        {
          params: { appid: appId, orgid: orgId },
        },
      );

      const subject = commResponse.data?.subject || "";
      const body = commResponse.data?.body || "";
      const date = commResponse.data?.date || "";
      // Return the data for immediate use
      return {
        subject,
        body,
        date,
      };
    } catch (err) {
      console.error("Error loading communication data:", err);
      return {
        subject: "",
        body: "",
        date: "",
      };
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Director Dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          onClick={handleBackClick}
          startIcon={<ArrowBackIcon />}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  if (!cabDetails) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="textSecondary">
          No CAB details found for this application
        </Typography>
        <Button
          variant="outlined"
          onClick={handleBackClick}
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
        >
          Go Back
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f8fafc",
        pt: 2,
        pb: 4,
      }}
    >
      {/* Header with Gradient Background */}
      <Paper
        elevation={0}
        sx={{
          background: "linear-gradient(135deg, #00024c 0%, #1a2b6b 100%)",
          color: "white",
          borderRadius: 0,
          mb: 3,
        }}
      >
        <Box sx={{ px: 3, py: 3 }}>
          {/* Breadcrumbs */}
          <Breadcrumbs
            sx={{
              mb: 2,
              "& .MuiBreadcrumbs-separator": { color: "rgba(255,255,255,0.7)" },
            }}
          >
            <Link
              component="button"
              variant="body2"
              onClick={handleBackClick}
              sx={{
                textDecoration: "none",
                color: "rgba(255,255,255,0.8)",
                "&:hover": { color: "white" },
              }}
            >
              Applications
            </Link>
            <Typography variant="body2" sx={{ color: "white" }}>
              Dashboard
            </Typography>
          </Breadcrumbs>

          {/* Header Content */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Avatar
                sx={{
                  bgcolor: "rgba(255,255,255,0.2)",
                  mr: 2,
                  width: 56,
                  height: 56,
                }}
              >
                <DashboardIcon sx={{ fontSize: 28 }} />
              </Avatar>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {cabDetails.laboratoryName}
                </Typography>

                <Typography variant="subtitle1" sx={{ opacity: 0.9 }}>
                  {cabDetails.applicationNo} • {cabDetails.labType} •
                  <Chip
                    label={`Standard: ${cabDetails.standard}`}
                    variant="outlined"
                    size="small"
                    sx={{
                      ml: 1,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.7)",
                    }}
                  />
                  <Chip
                    icon={
                      <Person2Outlined sx={{ color: "white !important" }} />
                    }
                    label="Contact Persons"
                    variant="outlined"
                    size="small"
                    onClick={handleOpenContactDialog}
                    sx={{
                      ml: 1,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderColor: "white",
                      },
                    }}
                  />
                  <Chip
                    icon={
                      <Person2Outlined sx={{ color: "white !important" }} />
                    }
                    label="Dealing Officer"
                    variant="outlined"
                    size="small"
                    onClick={handleOpenOfficerDialog}
                    sx={{
                      ml: 1,
                      color: "white",
                      borderColor: "rgba(255,255,255,0.7)",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: "rgba(255,255,255,0.1)",
                        borderColor: "white",
                      },
                    }}
                  />
                </Typography>
              </Box>
            </Box>

            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackClick}
              sx={{
                borderColor: "rgba(255,255,255,0.3)",
                color: "white",
                "&:hover": {
                  borderColor: "white",
                  backgroundColor: "rgba(255,255,255,0.1)",
                },
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ px: 2 }}>
        {/* Key Metrics Cards */}
        <Grid container spacing={3}>
          {/* Current Stage */}
          <Grid item xs={12} sm={6}>
            <Card
              sx={{
                background:
                  "linear-gradient(135deg, #fe8f4fff 0%, #fe3b00ff 100%)",
                color: "white",
                borderRadius: 3,
                boxShadow: "0 8px 32px rgba(79, 172, 254, 0.25)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 12px 40px rgba(79, 172, 254, 0.35)",
                },
              }}
            >
              <CardContent sx={{ p: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: "rgba(255,255,255,0.2)",
                      width: 48,
                      height: 48,
                    }}
                  >
                    <TimelineIcon sx={{ fontSize: 24 }} />
                  </Avatar>
                  <ScheduleIcon sx={{ fontSize: 20, opacity: 0.7 }} />
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, fontSize: 15, mb: 1 }}
                >
                  Current Stage
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  {cabDetails.stage || "N/A"} <br />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, fontSize: 15, mt: 1 }}
                  >
                    Last Updated
                  </Typography>{" "}
                  {formatDate(cabDetails.lastUpdated)} <br />
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, fontSize: 15, mt: 1 }}
                  >
                    Pending At
                  </Typography>{" "}
                  {cabDetails.PendingAt || "N/A"}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 700, fontSize: 15, mt: 1 }}
                  >
                    Submission Date
                  </Typography>{" "}
                  {formatDate(cabDetails.submissionDate)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} lg={6}>
            <Card
              sx={{
                borderRadius: 3,
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                border: "1px solid #e2e8f0",
              }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: "success.main",
                      mr: 2,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <DescriptionIcon />
                  </Avatar>
                  <Typography
                    variant="h5"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Quick Actions
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid
                    item
                    xs={12}
                    sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}
                  >
                    {/* <Button
                      variant="contained"
                      color="primary"
                      startIcon={<ScrutinyIcon />}
                      onClick={() => {
                        const params = new URLSearchParams({
                          appId: encryptedAppId,
                          orgId: encryptedOrgId,
                          labTypeId: encryptedLabTypeId
                        });
                        navigate(`/main/director/application/form8?${params.toString()}`);
                      }}
                    >
                      Form 8
                    </Button>
                    {applicationStage && applicationStage === '20' && (
                      <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<ScrutinyIcon />}
                        onClick={() => {
                          const params = new URLSearchParams({
                            appId: encryptedAppId,
                            orgId: encryptedOrgId,
                            labTypeId: encryptedLabTypeId
                          });
                          navigate(`/main/director/application/form9?${params.toString()}`);
                        }}
                      >
                        Form 9
                      </Button>
                    )} */}
                    {applicationStage && applicationStage === "7b" && (
                      <>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={handleApproveClick}
                        >
                          Forward Certificate to CEO
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={handleDisapproveClick}
                        >
                          Request Changes on Certificate
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<VisibilityIcon />}
                          onClick={handleViewCommunicationLetter}
                        >
                          View Communication Letter
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<VisibilityIcon />}
                          onClick={handleViewDecisionLetter}
                        >
                          View Decision Letter
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<DownloadIcon />}
                          onClick={handleDownloadCertificate}
                        >
                          Download Certificate
                        </Button>
                      </>
                    )}
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Main Content Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {/* Dealing Officer Details Button */}
          <Grid item xs={12} lg={4}>
            {/* Dialog for Dealing Officer Information */}
            <Dialog
              open={openOfficerDialog}
              onClose={handleCloseOfficerDialog}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Dealing Officer Information</DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: "primary.main",
                      mr: 2,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <BadgeIcon />
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Dealing Officer
                  </Typography>
                </Box>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box
                      sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2, mb: 2 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        Officer Name
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        {dealingOfficer?.name || "Not assigned"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box
                      sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2, mb: 2 }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        Email
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        {dealingOfficer?.email || "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ p: 2, bgcolor: "#f0f9ff", borderRadius: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: 1,
                        }}
                      >
                        Contact
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mt: 0.5 }}
                      >
                        {dealingOfficer?.mobile ||
                          dealingOfficer?.email ||
                          "N/A"}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseOfficerDialog}
                  color="primary"
                  variant="outlined"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>

          {/* Contact Person Details Button */}
          <Grid item xs={12} lg={4}>
            {/* Dialog for Contact Person Information */}
            <Dialog
              open={openContactDialog}
              onClose={handleCloseContactDialog}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle>Contact Person Information</DialogTitle>
              <DialogContent>
                <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                  <Avatar
                    sx={{
                      bgcolor: "secondary.main",
                      mr: 2,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <BusinessIcon />
                  </Avatar>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 600, color: "text.primary" }}
                  >
                    Laboratory Contacts
                  </Typography>
                </Box>

                {contactPersons.length > 0 ? (
                  <Grid container spacing={3}>
                    {contactPersons.map((contact, index) => (
                      <Grid item xs={12} md={6} key={index}>
                        <Card
                          variant="outlined"
                          sx={{
                            height: "100%",
                            border: contact.isPrimary
                              ? "2px solid #1976d2"
                              : "1px solid #e0e0e0",
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 2,
                              }}
                            >
                              <Avatar
                                sx={{
                                  bgcolor:
                                    contact.recordType === "lab_head_contact"
                                      ? "primary.main"
                                      : "success.main",
                                  mr: 2,
                                  width: 32,
                                  height: 32,
                                }}
                              >
                                <Person2Outlined sx={{ fontSize: 18 }} />
                              </Avatar>
                              <Box>
                                <Typography
                                  variant="h6"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {contact.recordType === "lab_head_contact"
                                    ? "Contact Person Details"
                                    : "CEO Details"}
                                </Typography>
                                {/* {contact.isPrimary && (
                                  <Chip 
                                    label="Primary" 
                                    size="small" 
                                    color="primary" 
                                    sx={{ mt: 0.5 }} 
                                  />
                                )} */}
                              </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontWeight: 600,
                                  textTransform: "uppercase",
                                  letterSpacing: 0.5,
                                }}
                              >
                                Name
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 500, mt: 0.5 }}
                              >
                                {contact.name || "Not available"}
                              </Typography>
                            </Box>

                            {contact.email && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  Email
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mt: 0.5 }}
                                >
                                  {contact.email}
                                </Typography>
                              </Box>
                            )}

                            {contact.mobile && (
                              <Box sx={{ mb: 2 }}>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: 0.5,
                                  }}
                                >
                                  Mobile
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 500, mt: 0.5 }}
                                >
                                  {contact.mobile}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ textAlign: "center", py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      No contact information available
                    </Typography>
                  </Box>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleCloseContactDialog}
                  color="secondary"
                  variant="outlined"
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
        </Grid>

        {/* Assessor Details Section */}
        <Grid item xs={12}>
          <AssessorDetailsView
            appId={encryptedAppId}
            orgId={encryptedOrgId}
            type="JD"
            stage={cabDetails.CurrentStage}
          />
        </Grid>

        {/* Reports Details Section */}
        <ReportsDetails appId={appId} orgId={orgId} />

        {/* Payment Details Section */}
        <PaymentDetails appId={appId} />

        {/* Email Notification Details Section */}
        <EmailNotificationDetails appId={appId} isCab={false} />
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert
          severity="success"
          sx={{ position: "fixed", top: 80, right: 20, zIndex: 9999 }}
        >
          {success}
        </Alert>
      )}

      {/* Approve Dialog */}
      <Dialog
        open={approveDialogOpen}
        onClose={() => !loading && setApproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CheckIcon color="success" />
            Forward to CEO
            {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {cabDetails && (
              <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  Application No: {cabDetails.applicationNo}
                </Typography>
              </Box>
            )}

            <FormControl fullWidth required disabled={loading || loadingCOList}>
              <InputLabel>Select CEO</InputLabel>
              <Select
                value={selectedCO}
                onChange={(e) => setSelectedCO(e.target.value)}
                label="Select CEO"
              >
                <MenuItem value="">--Select CEO--</MenuItem>
                {coList.map((co) => (
                  <MenuItem key={co.id} value={co.id}>
                    {co.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Forward Reason (Optional)"
              placeholder="Enter reason for approval..."
              value={forwardReason}
              onChange={(e) => setForwardReason(e.target.value)}
              disabled={loading}
            />

            <TextField
              fullWidth
              type="password"
              label="Enter Your Password"
              value={approvePassword}
              onChange={(e) => setApprovePassword(e.target.value)}
              disabled={loading}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setApproveDialogOpen(false);
              setSelectedCO("");
              setApprovePassword("");
              setForwardReason("");
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApproveSubmit}
            variant="contained"
            color="success"
            disabled={loading || loadingCOList}
            startIcon={loading ? <CircularProgress size={20} /> : <CheckIcon />}
          >
            {loading ? "Sending..." : "Send to CEO"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Disapprove Dialog */}
      <Dialog
        open={disapproveDialogOpen}
        onClose={() => !loading && setDisapproveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CloseIcon color="error" />
            Request Changes
            {loading && <CircularProgress size={20} sx={{ ml: 1 }} />}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 3 }}>
            {cabDetails && (
              <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 1 }}>
                <Typography variant="body2" fontWeight="bold">
                  Application No: {cabDetails.applicationNo}
                </Typography>
              </Box>
            )}

            <TextField
              fullWidth
              multiline
              rows={3}
              label="Forward Reason *"
              placeholder="Enter reason for requesting changes..."
              value={forwardReason}
              onChange={(e) => setForwardReason(e.target.value)}
              disabled={loading}
              required
              error={!forwardReason.trim() && error}
              helperText={
                !forwardReason.trim() && error
                  ? "Reason is required for requesting changes"
                  : ""
              }
            />

            <TextField
              fullWidth
              type="password"
              label="Enter Your Password"
              value={approvePassword}
              onChange={(e) => setApprovePassword(e.target.value)}
              disabled={loading}
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDisapproveDialogOpen(false);
              setSelectedCO("");
              setApprovePassword("");
              setForwardReason("");
            }}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDisapproveSubmit}
            variant="contained"
            color="error"
            disabled={loading || loadingCOList}
            startIcon={loading ? <CircularProgress size={20} /> : <CloseIcon />}
          >
            {loading ? "Requesting Changes..." : "Request Changes"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppDashboard;
